/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {dispatchCustomEvent, EventType, navigate} from '@dvrd/dvr-controls';
import type User from '../models/userModel';
import type Account from '../models/accountModel';
import {HeaderCrumb} from './interfaces';
import {isAndroid, isIOS} from './pwaUtil';

export const onRegister = () => {
    dispatchCustomEvent('onRegister');
};

export const onSignInGoogle = (googleUser: any) => {
    dispatchCustomEvent('onSignInGoogle', googleUser);
};

export const onAddAccount = () => {
    navigate('/account-instellen');
};

export const onLogout = (redirect?: string) => {
    dispatchCustomEvent('onLogout', redirect);
};

export const onResetDashboard = () => {
    dispatchCustomEvent('onResetDashboard');
};

export const onInvoicePreview = (pdfData: string) => {
    if (isIOS() || isAndroid()) {
        // Mobile browsers don't render PDFs inside <object>/<embed> — the
        // preview dialog would only show the OS "Open" placeholder. Hand the
        // blob straight to the native viewer instead. No revokeObjectURL:
        // revoking here would break the viewer tab; the blob lives until the
        // SPA unloads.
        const viewer = window.open(pdfData, '_blank');
        // Pop-up blocked (the fetch broke the user-gesture chain) — open in
        // the current tab; the browser back button returns to the app.
        if (!viewer) window.location.assign(pdfData);
        return;
    }
    dispatchCustomEvent('onInvoicePreview', pdfData);
};

export const onPreviewMail = (template: string) => {
    dispatchCustomEvent('onPreviewMail', template);
};

export const onPingStatusEvent = () => {
    dispatchCustomEvent('onPingStatusEvent');
};

export const onForcePing = () => {
    dispatchCustomEvent('onForcePing');
};

export const onStopPing = () => {
    dispatchCustomEvent('onStopPing');
};

export const onUserEvent = (user: User, eventType: EventType) => {
    dispatchCustomEvent('onUserEvent', {user, eventType});
};

export const onAccountEvent = (account: Account, eventType: EventType) => {
    dispatchCustomEvent('onAccountEvent', {account, eventType});
};

export const onSetCrumbs = (crumbs: HeaderCrumb[]) => {
    dispatchCustomEvent('onSetCrumbs', crumbs);
};

export const onTrialEnd = () => {
    dispatchCustomEvent('onTrialEnd');
};

export const onPremiumFeature = () => {
    dispatchCustomEvent('onPremiumFeature');
};

export const onConfirmCheckout = () => {
    dispatchCustomEvent('onConfirmCheckout');
};

export const onApiInOut = (type: 'intro' | 'outro') => {
    dispatchCustomEvent('onApiInOut', type);
};

export const onRecommend2Fa = () => {
    dispatchCustomEvent('onRecommend2Fa');
};

export const onEmailStepStats = (emailStepId: string) => {
    dispatchCustomEvent('onEmailStepStats', emailStepId);
};

export const onGlobalLoad = (load: boolean) => {
    dispatchCustomEvent('onGlobalLoad', load);
};

export function onChangeDashboardTab() {
    window.setTimeout(() => {
        dispatchCustomEvent('onChangeDashboardTab');
    });
}

export function updateInvoiceRequestCount(count: number) {
    dispatchCustomEvent('updateInvoiceRequestCount', count);
}
